import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Share2, Smartphone, ChevronDown, ChevronUp } from 'lucide-react';

const QRCodePage = () => {
  const navigate = useNavigate();
  const [showIOS, setShowIOS] = useState(false);
  const [showAndroid, setShowAndroid] = useState(false);

  const handleDownload = () => {
    // Download HIGH RESOLUTION version for printing
    const link = document.createElement('a');
    link.href = '/qr-card.png';
    link.download = 'Ananda-Sporting-Club-QR-HD.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadQROnly = () => {
    // Download just the QR code
    const link = document.createElement('a');
    link.href = '/qr-code.png';
    link.download = 'Ananda-Sport-QR-Code.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Ananda Sporting Club',
          text: 'Unisciti al nostro circolo di tennis!',
          url: window.location.origin
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.origin);
      alert('Link copiato!');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-lg mx-auto animate-fadeIn" data-testid="qr-code-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <h1 className="font-outfit text-2xl font-semibold text-slate-900">
          QR Code
        </h1>
      </div>

      {/* QR Card Preview */}
      <div className="bg-white rounded-3xl p-8 border border-slate-100 text-center mb-6">
        <img 
          src="/qr-card.png" 
          alt="QR Code Ananda Sporting Club"
          className="w-full max-w-sm mx-auto mb-6 shadow-lg rounded-2xl"
        />
        <p className="text-slate-600 mb-2">
          Inquadra il QR code per unirti al circolo
        </p>
        <p className="text-sm text-slate-400">
          Risoluzione alta per stampa biglietti da visita
        </p>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <button
          onClick={handleDownload}
          data-testid="download-qr"
          className="w-full flex items-center justify-center gap-2 py-4 bg-sky-400 text-white rounded-2xl font-medium hover:bg-sky-500 transition-all"
        >
          <Download className="w-5 h-5" />
          Scarica Biglietto HD (per stampa)
        </button>
        
        <button
          onClick={handleDownloadQROnly}
          data-testid="download-qr-only"
          className="w-full flex items-center justify-center gap-2 py-4 bg-white text-slate-700 border border-slate-200 rounded-2xl font-medium hover:bg-slate-50 transition-all"
        >
          <Download className="w-5 h-5" />
          Scarica solo QR Code
        </button>
        
        <button
          onClick={handleShare}
          data-testid="share-qr"
          className="w-full flex items-center justify-center gap-2 py-4 bg-white text-slate-700 border border-slate-200 rounded-2xl font-medium hover:bg-slate-50 transition-all"
        >
          <Share2 className="w-5 h-5" />
          Condividi Link
        </button>
      </div>

      {/* Installa come App */}
      <div className="mt-8 space-y-3" data-testid="install-guide">
        <div className="flex items-center gap-2 mb-1">
          <Smartphone className="w-5 h-5 text-slate-700" />
          <h2 className="font-outfit text-lg font-semibold text-slate-900">Installa come App</h2>
        </div>
        <p className="text-sm text-slate-500 mb-3">
          Aggiungi Ananda Sport alla schermata Home del tuo telefono per aprirla come una vera app!
        </p>

        {/* iOS Instructions */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <button
            onClick={() => setShowIOS(!showIOS)}
            data-testid="ios-guide-toggle"
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-900 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              </div>
              <span className="font-medium text-slate-900">iPhone / iPad (Safari)</span>
            </div>
            {showIOS ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          {showIOS && (
            <div className="px-4 pb-4 space-y-3" data-testid="ios-guide-steps">
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0 text-sky-600 font-bold text-sm">1</div>
                <div>
                  <p className="text-sm text-slate-700">Apri il sito con <strong>Safari</strong></p>
                  <p className="text-xs text-slate-400 mt-0.5">Non funziona con Chrome o altri browser su iOS</p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0 text-sky-600 font-bold text-sm">2</div>
                <div>
                  <p className="text-sm text-slate-700">Tocca l'icona <strong>Condividi</strong> in basso</p>
                  <p className="text-xs text-slate-400 mt-0.5">Il quadrato con la freccia verso l'alto</p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0 text-sky-600 font-bold text-sm">3</div>
                <div>
                  <p className="text-sm text-slate-700">Scorri e tocca <strong>"Aggiungi alla schermata Home"</strong></p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0 text-sky-600 font-bold text-sm">4</div>
                <div>
                  <p className="text-sm text-slate-700">Tocca <strong>"Aggiungi"</strong> in alto a destra</p>
                  <p className="text-xs text-slate-400 mt-0.5">L'icona apparirà nella schermata Home!</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Android Instructions */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <button
            onClick={() => setShowAndroid(!showAndroid)}
            data-testid="android-guide-toggle"
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-500 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M17.523 2.273l1.74-1.74a.5.5 0 00-.707-.707l-1.956 1.956A9.97 9.97 0 0012 .5a9.97 9.97 0 00-4.6 1.282L5.444.174a.5.5 0 00-.707.707l1.74 1.74A9.955 9.955 0 002 10v1h20v-1a9.955 9.955 0 00-4.477-7.727zM8.5 8a1 1 0 110-2 1 1 0 010 2zm7 0a1 1 0 110-2 1 1 0 010 2zM2 12v7a3 3 0 003 3h14a3 3 0 003-3v-7H2z"/></svg>
              </div>
              <span className="font-medium text-slate-900">Android (Chrome)</span>
            </div>
            {showAndroid ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          {showAndroid && (
            <div className="px-4 pb-4 space-y-3" data-testid="android-guide-steps">
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 text-emerald-600 font-bold text-sm">1</div>
                <div>
                  <p className="text-sm text-slate-700">Apri il sito con <strong>Chrome</strong></p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 text-emerald-600 font-bold text-sm">2</div>
                <div>
                  <p className="text-sm text-slate-700">Tocca i <strong>tre puntini</strong> in alto a destra</p>
                  <p className="text-xs text-slate-400 mt-0.5">Il menu di Chrome</p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 text-emerald-600 font-bold text-sm">3</div>
                <div>
                  <p className="text-sm text-slate-700">Tocca <strong>"Aggiungi a schermata Home"</strong></p>
                  <p className="text-xs text-slate-400 mt-0.5">Oppure "Installa app" se disponibile</p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 text-emerald-600 font-bold text-sm">4</div>
                <div>
                  <p className="text-sm text-slate-700">Conferma toccando <strong>"Aggiungi"</strong></p>
                  <p className="text-xs text-slate-400 mt-0.5">L'icona apparirà nella schermata Home!</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tip */}
      <div className="mt-6 bg-sky-50 rounded-2xl p-4 text-sm text-sky-700">
        <p className="font-medium mb-2">Suggerimento</p>
        <p>
          Stampa il QR code e mettilo nella bacheca del circolo o sui biglietti da visita 
          per permettere ai nuovi giocatori di iscriversi facilmente!
        </p>
      </div>
    </div>
  );
};

export default QRCodePage;
